import { Link } from "react-router-dom";
import { Quote, TrendingDown, Leaf, Wallet, Zap, Calendar, MapPin, Trophy, ArrowRight } from "lucide-react";
import { FadeUp } from "@/components/Motion";
import CTASection from "@/components/CTASection";
import { IMAGES } from "@/lib/seed-content";

const METRICS = [
  { icon: Wallet, value: "£0", label: "Upfront capital", note: "Funded, installed and owned by SEED" },
  { icon: TrendingDown, value: "~60%", label: "Projected electricity reduction", note: "Indicative range — site-specific modelling" },
  { icon: Leaf, value: "~35t", label: "Projected CO₂ avoided / yr", note: "Equivalent to ~19 transatlantic flights" },
  { icon: Zap, value: "25 yrs", label: "Fixed kWh rate", note: "Locked in at signing. Maintenance included throughout" },
];

const TIMELINE = [
  { step: "Audit", body: "Feasibility study completed in under three weeks. Roof structural review, consumption analysis and shading model." },
  { step: "Approval", body: "Committee presented with a single-page recommendation. Plain-English contract reviewed by the club's solicitor." },
  { step: "Install", body: "Tier-1 panels and inverters installed by experienced commercial solar engineers — scheduled around the fixture calendar." },
  { step: "Live", body: "System commissioned, monitored remotely, and onboarded to the club's online dashboard. Maintenance ongoing." },
];

export default function CaseStudy() {
  return (
    <div data-testid="case-study-page">
      {/* HERO */}
      <section className="relative overflow-hidden navy-panel grain" data-testid="case-hero">
        <div className="container-seed pt-20 pb-24 lg:pt-24 lg:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <FadeUp>
                <div className="text-overline text-emerald-400">Case Study · Sports Club</div>
              </FadeUp>
              <FadeUp delay={1}>
                <h1 className="mt-4 font-display font-extrabold text-white tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
                  Chichester RFC:<br />a 25-year answer to a <span className="text-emerald-400">recurring committee headache</span>.
                </h1>
              </FadeUp>
              <FadeUp delay={2}>
                <p className="mt-6 text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed">
                  A historic rugby club, a volunteer committee, and an energy bill that demanded a fresh
                  negotiation every season. Here's how SEED took it off the agenda permanently.
                </p>
              </FadeUp>
              <FadeUp delay={3}>
                <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-300">
                  <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-emerald-400" /> Chichester, West Sussex</span>
                  <span className="inline-flex items-center gap-2"><Trophy className="w-4 h-4 text-emerald-400" /> Member-owned sports club</span>
                  <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4 text-emerald-400" /> SEED install: live</span>
                </div>
              </FadeUp>
            </div>

            <FadeUp delay={2} className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                <img
                  src={IMAGES.commercialRoof}
                  alt="Commercial rooftop solar installation — representative imagery"
                  className="w-full h-[420px] object-cover"
                />
              </div>
              <p className="mt-3 text-[11px] text-slate-400 leading-relaxed text-center lg:text-right">
                Representative imagery. Full installation gallery available in the case study pack.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="container-seed -mt-12 relative z-10" data-testid="case-metrics">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-lg p-6 md:p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {METRICS.map((m, i) => (
              <FadeUp key={m.label} delay={i}>
                <div>
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                    <m.icon className="w-5 h-5 text-emerald-600" strokeWidth={2} />
                  </div>
                  <div className="mt-4 font-display font-extrabold text-slate-900 text-3xl sm:text-4xl tracking-tight">{m.value}</div>
                  <div className="mt-1 text-slate-900 font-semibold text-sm">{m.label}</div>
                  <div className="mt-1 text-slate-500 text-xs leading-relaxed">{m.note}</div>
                </div>
              </FadeUp>
            ))}
          </div>
          <p className="mt-6 pt-5 border-t border-slate-100 text-[11px] text-slate-500 leading-relaxed">
            Figures shown are projections from SEED's pre-install feasibility model and are indicative only.
            Verified post-install performance data is published in the full case study pack on request.
          </p>
        </div>
      </section>

      {/* NARRATIVE */}
      <section className="container-seed section-pad" data-testid="case-narrative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="text-overline">The story</div>
            <h2 className="mt-3 font-display font-bold text-slate-900 text-3xl sm:text-4xl tracking-tight leading-[1.1]">
              A volunteer committee, a clubhouse roof, and a model that finally worked.
            </h2>
            <div className="mt-8 space-y-5 text-slate-700 leading-relaxed">
              <p>
                Like most established sports clubs, Chichester RFC's committee had spent years circling the same
                question: how do you reduce the clubhouse's electricity bill without raiding the capital reserves
                that are supposed to be funding junior rugby, the bar refurbishment, and pitch maintenance?
              </p>
              <p>
                Three previous solar proposals had stalled — each one demanded six-figure capital outlay or
                long-tail performance risk that the committee couldn't, in good conscience, take on.
              </p>
              <p>
                SEED was different on one specific point: <strong className="text-slate-900">the committee never had to write a cheque</strong>.
                We funded, installed and now own the system. The club pays only for the electricity it uses, at a
                kWh rate locked for 25 years and indexed to inflation — not to wholesale markets. Maintenance
                and monitoring are entirely on our side of
                the agreement.
              </p>
              <p>
                A year in, the club's energy line is the most predictable item in the accounts — and the
                committee has reclaimed an evening per quarter previously spent comparing tariffs.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <FadeUp>
              <div className="bg-slate-900 text-white rounded-2xl p-8 lg:p-10 relative overflow-hidden">
                <Quote className="absolute -top-4 -right-4 w-24 h-24 text-emerald-500/10" />
                <p className="relative font-display text-xl sm:text-2xl leading-snug">
                  "It's the first energy decision in twenty years that we haven't had to revisit. Signed once,
                  and the matter is closed for a generation."
                </p>
                <div className="relative mt-6 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center text-emerald-400 font-bold">SC</div>
                  <div>
                    <div className="font-semibold text-white text-sm">Sports Club Committee</div>
                    <div className="text-slate-400 text-xs">Representative testimonial</div>
                  </div>
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={1}>
              <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6">
                <div className="text-overline">At a glance</div>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  <li>• Decision made in two committee meetings</li>
                  <li>• Install completed outside the playing season</li>
                  <li>• Zero impact on the club's restricted reserves</li>
                  <li>• Live monitoring dashboard for the trustees</li>
                </ul>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="bg-slate-100/60 border-y border-slate-200" data-testid="case-timeline">
        <div className="container-seed section-pad">
          <div className="text-overline">From audit to live in under four months</div>
          <h2 className="mt-3 font-display font-bold text-slate-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
            The four-step delivery model.
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-5">
            {TIMELINE.map((t, i) => (
              <FadeUp key={t.step} delay={i}>
                <div className="bg-white border border-slate-200 rounded-xl p-6 h-full relative">
                  <div className="text-overline">Stage {String(i + 1).padStart(2, "0")}</div>
                  <h3 className="mt-2 font-display font-bold text-slate-900 text-xl">{t.step}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed text-sm">{t.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>

          <div className="mt-12">
            <Link
              to="/contact"
              data-testid="case-cta-link"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 px-6 rounded-lg group"
            >
              Book the same conversation for your club
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      <CTASection
        overline="Replicate the model"
        title="Your clubhouse, your roof, the same outcome."
        subtitle="Every install is bespoke — but the contractual structure that worked at Chichester RFC is available to any UK sports club that qualifies."
        ctaLabel="Book Assessment"
        testId="case-cta-section"
      />
    </div>
  );
}
